import { useMutation, useQuery } from '@tanstack/react-query';

import { QUERY_KEYS, usePatronSession } from '@app/hooks';
import { makeRequest } from '@app/utils';
import { SORT_DIRECTION, SORT_DIRECTION_REVERSED } from '@app/constants';

export interface Project {
  id: string;
  name: string;
  tags: string[];
  savedInstances: unknown[];
  projectSearches: unknown[];
  totalRecords: number;
}

export interface ProjectsDto<T = Project> {
  projects: T[];
}

export interface ProjectCreationDto {
  name: string;
  description?: string;
}

export interface AddInstanceToProjectPayloadDto {
  projectId: string;
  instanceIds: { instanceId: string }[];
}

const createUrl = (externalSystemId: string) =>
  `/opac-patron/account/${externalSystemId}/projects`;

interface ProjectsFetchProps {
  search?: string;
  sortDirection?: SORT_DIRECTION;
}

export const useProjectsFetch = ({
  search: searchQuery,
  sortDirection = SORT_DIRECTION.desc,
}: ProjectsFetchProps = {}) => {
  const { externalSystemId } = usePatronSession();
  const url = createUrl(externalSystemId as string);

  return useQuery<ProjectsDto, unknown, Project[]>(
    [QUERY_KEYS.projects, externalSystemId, searchQuery, sortDirection],
    () =>
      makeRequest<ProjectsDto>({
        url,
        params: {
          search: searchQuery,
          sort: SORT_DIRECTION_REVERSED[sortDirection],
        },
      }),
    {
      enabled: Boolean(externalSystemId),
      select: ({ projects }) =>
        projects.map(project => ({
          ...project,
        })),
    }
  );
};

export const useCreateProjectsMutation = () => {
  const { externalSystemId } = usePatronSession();
  const url = createUrl(externalSystemId as string);

  return useMutation((data: ProjectCreationDto) =>
    makeRequest({
      url,
      method: 'POST',
      data,
    })
  );
};

export const useEditProjectsMutation = ({ id }) => {
  const { externalSystemId } = usePatronSession();
  const url = `${createUrl(externalSystemId as string)}/${id}`;

  return useMutation((data: ProjectCreationDto) =>
    makeRequest({
      url,
      method: 'PUT',
      data,
    })
  );
};

export const useAddInstancesToProjectMutation = () => {
  const { externalSystemId } = usePatronSession();

  return useMutation(
    ({ instanceIds, projectId }: AddInstanceToProjectPayloadDto) =>
      makeRequest({
        url: `${createUrl(
          externalSystemId as string
        )}/${projectId}/instance-actions`,
        method: 'POST',
        data: instanceIds,
      })
  );
};
